import { isUndefined } from "lodash"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useOnClickOutside from "use-onclickoutside"

interface Option {
  name: string
  url: string
  selected: boolean
  cancelled?: boolean
  dates?: string
}

interface SelectorDropdownProps {
  open: boolean
  options: Option[]
  selectOption: (option: Option) => void
  selected: Option | undefined
}

const SelectorDropdown = ({ open, options, selectOption, selected }: SelectorDropdownProps) => {
  const selectedRef = useCallback((node: HTMLDivElement | null, option: Option) => {
    if (node && option.selected) {
      node.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" })
    }
  }, [])

  return <div role="listbox" className={`raceweekSelector__options${open ? "" : " raceweekSelector__options--closed"}`}>
    {options.map((option, index) => {
      return (
        <div
          ref={(node) => selectedRef(node, option)}
          key={index}
          role="option"
          className={`raceweekSelector__options__item ${selected?.name === option.name ? "raceweekSelector__options__item--selected" : ""}`}
          onClick={() => selectOption(option)}
        >
          <span>
            <span>{option.name}</span>
            {option.dates && <span className="raceweekSelector__options__item__datesText">{option.dates}</span>}
          </span>
        </div>
      )
    })}
  </div>
}

interface RaceweekSelectorProps {
  options: Option[]
}

const RaceweekSelector: React.FC<RaceweekSelectorProps> = ({ options }) => {
  const [open, setOpen] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => { if (open) setOpen(false) })

  const [selected, setSelected] = useState(
    options.find((option) => option.selected)
  )

  const { prevUrl, nextUrl } = useMemo(() => {
    const selectedIndex = options.findIndex((option) => selected?.name === option.name)
    
    return {
      prevUrl: selectedIndex >= 0 ? options[selectedIndex - 1]?.url : undefined,
      nextUrl: selectedIndex >= 0 ? options[selectedIndex + 1]?.url : undefined,
    }
  }, [options, selected])

  const selectOption = useCallback((option: Option) => {
    setOpen(false)
    setSelected(option)
    window.location.href = option.url
  }, [])

  const toPreviousPage = useCallback(() => {
    if (prevUrl) {
      window.location.href = prevUrl
    }
  }, [prevUrl])

  const toNextPage = useCallback(() => {
    if (nextUrl) {
      window.location.href = nextUrl
    }
  }, [nextUrl])

  const toggleDropdown = useCallback(() => {
    setOpen((val) => !val)
  }, [])

  return <div ref={ref} className="raceweekSelector">
    <button
      disabled={isUndefined(prevUrl)}
      className={"raceweekSelector__arrowButton raceweekSelector__arrowButton--prev" + (isUndefined(prevUrl) ? " raceweekSelector__arrowButton--inactive" : "")}
      onClick={toPreviousPage}
    />
    <div className="raceweekSelector__container">
      <button
        className={[
          "raceweekSelector__button",
          open ? "raceweekSelector__button--open" : "",
        ].join(" ")}
        aria-expanded={open}
        onClick={toggleDropdown}
      >
        <div className="raceweekSelector__button__text">
          <span>{selected?.name}</span>
          {selected?.dates && <span>{selected?.dates}</span>}
        </div>
        <div className="raceweekSelector__button__icon" />
      </button>
      <SelectorDropdown open={open} options={options} selectOption={selectOption} selected={selected} />
    </div>
    <button
      disabled={isUndefined(nextUrl)}
      className={"raceweekSelector__arrowButton raceweekSelector__arrowButton--next" + (isUndefined(nextUrl) ? " raceweekSelector__arrowButton--inactive" : "")}
      onClick={toNextPage}
    />
  </div>
}

export default RaceweekSelector
